/**
 * RolesPage — 角色权限配置页
 * Admin 可以修改每个角色对每个资源的访问权限，保存到 configuration.role_permissions
 */
import { useState } from "react";
import { useNotify } from "ra-core";
import { Shield, Save, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { useConfigurationContext, useConfigurationUpdater } from "../root/ConfigurationContext";
import { defaultRolePermissions } from "../root/defaultConfiguration";
import type { Permission, RolePermissions, SaleRole } from "../types";

// ── Constants ─────────────────────────────────────────────────────────────────

export const ROLE_LABELS: Record<SaleRole, string> = {
  admin:        "管理员",
  manager:      "销售经理",
  sales:        "销售员",
  merchandiser: "跟单员",
  finance:      "财务",
};

const ROLE_COLORS: Record<SaleRole, string> = {
  admin:        "text-blue-700",
  manager:      "text-purple-700",
  sales:        "text-green-700",
  merchandiser: "text-orange-700",
  finance:      "text-yellow-700",
};

const PERM_OPTIONS: { value: Permission; label: string; color: string }[] = [
  { value: "all",  label: "全部",     color: "bg-green-100 text-green-800" },
  { value: "own",  label: "仅自己",   color: "bg-blue-100 text-blue-800" },
  { value: "read", label: "只读",     color: "bg-gray-100 text-gray-700" },
  { value: "none", label: "无权限",   color: "bg-red-50 text-red-400" },
];

const RESOURCE_GROUPS: {
  group: string;
  resources: { key: string; label: string }[];
}[] = [
  {
    group: "客户管理",
    resources: [
      { key: "contacts",  label: "联系人" },
      { key: "companies", label: "公司" },
      { key: "deals",     label: "交易看板" },
    ],
  },
  {
    group: "销售流程",
    resources: [
      { key: "inquiries",  label: "询盘" },
      { key: "quotations", label: "报价单" },
      { key: "orders",     label: "订单" },
      { key: "members",    label: "会员/VIP" },
    ],
  },
  {
    group: "供应链",
    resources: [
      { key: "products",            label: "产品目录" },
      { key: "suppliers",           label: "供应商" },
      { key: "purchase_orders",     label: "采购单" },
      { key: "quality_inspections", label: "质检记录" },
      { key: "logistics",           label: "物流跟踪" },
      { key: "returns",             label: "退货换货" },
    ],
  },
  {
    group: "财务",
    resources: [
      { key: "ar",           label: "应收账款" },
      { key: "expenses",     label: "费用报销" },
      { key: "complaints",   label: "投诉索赔" },
      { key: "factory_cost", label: "工厂成本（字段）" },
    ],
  },
  {
    group: "分析与系统",
    resources: [
      { key: "reports",   label: "销售报表" },
      { key: "workflow",  label: "工作流" },
      { key: "audit_log", label: "审计日志" },
      { key: "settings",  label: "系统设置" },
    ],
  },
];

const EDITABLE_ROLES: SaleRole[] = ["manager", "sales", "merchandiser", "finance"];

// ── Permission Select Cell ────────────────────────────────────────────────────

const PermCell = ({
  value,
  onChange,
  locked,
}: {
  value: Permission;
  onChange: (v: Permission) => void;
  locked?: boolean;
}) => {
  const opt = PERM_OPTIONS.find((o) => o.value === value) ?? PERM_OPTIONS[3];
  if (locked) {
    return (
      <td className="px-3 py-2 text-center">
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${opt.color}`}>
          {opt.label}
        </span>
      </td>
    );
  }
  return (
    <td className="px-3 py-2 text-center">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as Permission)}
        className={`text-xs rounded-full px-2 py-0.5 font-medium border-0 outline-none cursor-pointer ${opt.color} appearance-none text-center`}
      >
        {PERM_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </td>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

export const RolesPage = () => {
  const notify = useNotify();
  const config = useConfigurationContext();
  const updateConfig = useConfigurationUpdater();

  const [perms, setPerms] = useState<RolePermissions>(() => ({
    ...defaultRolePermissions,
    ...config.rolePermissions,
  }));
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const handleChange = (resource: string, role: SaleRole, value: Permission) => {
    setPerms((prev) => ({
      ...prev,
      [resource]: { ...prev[resource], [role]: value },
    }));
    setDirty(true);
  };

  const handleReset = () => {
    setPerms({ ...defaultRolePermissions });
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const { error } = await getSupabaseClient()
      .from("configuration")
      .update({ role_permissions: perms })
      .eq("id", 1);

    if (error) {
      notify("保存失败: " + error.message, { type: "error" });
    } else {
      // Update local store immediately so canAccess picks up new values
      updateConfig((prev) => ({ ...prev, rolePermissions: perms }));
      notify("权限配置已保存", { type: "success" });
      setDirty(false);
    }
    setSaving(false);
  };

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="w-6 h-6" /> 角色权限配置
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            配置每个角色对各模块的访问权限。管理员（Admin）始终拥有全部权限。
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleReset} className="gap-1">
            <RotateCcw className="w-3.5 h-3.5" /> 恢复默认
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving || !dirty} className="gap-1">
            <Save className="w-3.5 h-3.5" />
            {saving ? "保存中..." : "保存配置"}
          </Button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex gap-3 flex-wrap">
        {PERM_OPTIONS.map((o) => (
          <span key={o.value} className={`text-xs px-2.5 py-1 rounded-full font-medium ${o.color}`}>
            {o.label}
          </span>
        ))}
        <span className="text-xs text-muted-foreground self-center ml-2">
          "仅自己" = 可操作自己创建的记录，他人不可见
        </span>
      </div>

      {/* Matrix table per group */}
      {RESOURCE_GROUPS.map((group) => (
        <div key={group.group} className="bg-card border rounded-lg overflow-hidden">
          <div className="px-4 py-2.5 bg-muted/50 border-b">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {group.group}
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left px-4 py-2.5 text-xs text-muted-foreground font-medium w-40">
                    模块
                  </th>
                  {/* Admin locked column */}
                  <th className={`px-3 py-2.5 text-xs font-semibold text-center w-24 ${ROLE_COLORS.admin}`}>
                    {ROLE_LABELS.admin}
                    <div className="text-[10px] font-normal text-muted-foreground">（锁定）</div>
                  </th>
                  {EDITABLE_ROLES.map((role) => (
                    <th key={role} className={`px-3 py-2.5 text-xs font-semibold text-center w-28 ${ROLE_COLORS[role]}`}>
                      {ROLE_LABELS[role]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {group.resources.map((res, i) => {
                  const rowPerms = perms[res.key] ?? defaultRolePermissions[res.key] ?? {};
                  return (
                    <tr key={res.key} className={i % 2 === 0 ? "" : "bg-muted/20"}>
                      <td className="px-4 py-2 text-sm font-medium">{res.label}</td>
                      {/* Admin always "all" */}
                      <PermCell value="all" onChange={() => {}} locked />
                      {EDITABLE_ROLES.map((role) => (
                        <PermCell
                          key={role}
                          value={(rowPerms[role] as Permission) ?? "none"}
                          onChange={(v) => handleChange(res.key, role, v)}
                        />
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {dirty && (
        <p className="text-xs text-orange-600 text-right">
          ⚠ 有未保存的更改
        </p>
      )}
    </div>
  );
};

RolesPage.path = "/settings/roles";
